/**
 * PURPOSE: Mostrar fases y concentrar las herramientas amigables de preparación competitiva.
 * RESPONSIBILITIES: Cargar deporte/fases/contexto, aplicar rol/cierre y abrir administradores de participantes, partidos y posiciones.
 * COLLABORATORS: CardFase, PhaseActionsBar y backend /phase-tools.
 * ROUTE: winscore/src/area/FasesArea/FasesArea.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import API_BASE_URL from "../../config/config.js";

import CardFase from "../../components/Cards/CardFase/CardFase";
import Area from "./SportIndividual/Area.jsx";
import CardFaseIndividual from "../../components/Cards/CardFase/CardFaseIndividual.jsx";
import PhaseActionsBar from "./components/PhaseActionsBar.jsx";
import ParticipantsManagerModal from "./components/ParticipantsManagerModal.jsx";
import MatchPlannerModal from "./components/MatchPlannerModal.jsx";
import PositionsCreationModal from "./components/PositionsCreationModal.jsx";
import PhaseSummaryModal from "./components/PhaseSummaryModal.jsx";
import ProgrammingCenterModal from "./ProgrammingCenter/ProgrammingCenterModal.jsx";
import { getPhaseToolsContext } from "../../services/phaseToolsService.js";
import { useProgrammingCenterStatus } from "../../hooks/useProgrammingCenterStatus.js";
import { isSuperAdmin } from "../../utils/authUser.js";

const MANAGEMENT_ROLES = new Set([1, 2, 4]);

const FasesArea = () => {
  const { idevent, idsport } = useParams();
  const { user } = useSelector((state) => state.auth);
  const [sport, setSport] = useState(null);
  const [phases, setPhases] = useState([]);
  const [toolsContext, setToolsContext] = useState(null);
  const [loadingTools, setLoadingTools] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [programmingCenterOpen, setProgrammingCenterOpen] = useState(false);
  // FTR-0028 D-02/§7: el flag lo confirma el backend (nunca una variable de build) y el rol 1 es
  // solo una pista de interfaz. El módulo administrativo vuelve a comprobar ambos en cada petición.
  const programmingCenterStatus = useProgrammingCenterStatus();

  const fetchSport = useCallback(async () => {
    if (!idsport) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/sport/`, {
        params: { idsport },
      });
      setSport(response.data);
    } catch (error) {
      console.error("Error fetching sport", error);
    }
  }, [idsport]);

  const fetchPhases = useCallback(async () => {
    if (!idevent || !idsport) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/phases`, {
        idevent: Number(idevent),
        idsport: Number(idsport),
      });
      setPhases(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching phases", error);
    }
  }, [idevent, idsport]);

  const canManageByRole = MANAGEMENT_ROLES.has(Number(user?.rol));
  const isCollective = [1, 2].includes(Number(sport?.typeSport));

  const fetchToolsContext = useCallback(async ({ notify = false } = {}) => {
    if (!idevent || !idsport || !canManageByRole || !isCollective) return null;
    try {
      setLoadingTools(true);
      const data = await getPhaseToolsContext({ idevent, idsport });
      setToolsContext(data);
      return data;
    } catch (error) {
      console.error("Error fetching phase tools context", error);
      setToolsContext(null);
      if (notify) {
        toast.error(
          error?.response?.data?.message ||
            "No se pudo actualizar la información de Fases."
        );
      }
      return null;
    } finally {
      setLoadingTools(false);
    }
  }, [canManageByRole, idevent, idsport, isCollective]);

  useEffect(() => {
    fetchSport();
    fetchPhases();
  }, [fetchPhases, fetchSport]);

  useEffect(() => {
    fetchToolsContext();
  }, [fetchToolsContext]);

  useEffect(() => {
    const handleConfigUpdated = (event) => {
      const detail = event.detail || {};
      if (
        Number(detail.idevent) !== Number(idevent) ||
        Number(detail.idsport) !== Number(idsport)
      ) {
        return;
      }
      setToolsContext((current) =>
        current
          ? {
              ...current,
              config: {
                ...current.config,
                ...(detail.state !== undefined
                  ? {
                      state: Number(detail.state),
                      isClosed: Number(detail.state) === 2,
                    }
                  : {}),
              },
            }
          : current
      );
    };
    window.addEventListener("winscore:config-category-updated", handleConfigUpdated);
    return () =>
      window.removeEventListener(
        "winscore:config-category-updated",
        handleConfigUpdated
      );
  }, [idevent, idsport]);

  const handleChanged = async () => {
    await Promise.all([fetchToolsContext(), fetchPhases()]);
  };

  const openTool = async (key, setOpen) => {
    try {
      setLoadingAction(key);
      const refreshed = await fetchToolsContext({ notify: true });
      if (refreshed) setOpen(true);
    } finally {
      setLoadingAction(null);
    }
  };

  if (sport && Number(sport.typeSport) === 3) {
    return (
      <>
        <div className="grid grid-cols-1 justify-items-center gap-4 md:grid-cols-1 lg:grid-cols-1">
          <Area idevent={idevent} idsport={idsport} />
        </div>
        <div className="mt-5 grid grid-cols-1 justify-items-center gap-4 md:grid-cols-2 lg:grid-cols-2">
          <CardFaseIndividual />
        </div>
      </>
    );
  }

  const showActions = canManageByRole && isCollective;
  const programmingCenterAvailable =
    isCollective &&
    isSuperAdmin(user) &&
    !programmingCenterStatus.loading &&
    programmingCenterStatus.enabled;
  const actionsDisabled = loadingTools || !toolsContext || toolsContext.config?.isClosed;
  const summaryDisabled = loadingTools || !toolsContext;

  return (
    <>
      {showActions ? (
        <PhaseActionsBar
          disabled={actionsDisabled}
          closed={Boolean(toolsContext?.config?.isClosed)}
          loading={loadingTools}
          loadingAction={loadingAction}
          summaryDisabled={summaryDisabled}
          onParticipants={() =>
            openTool("participants", setParticipantsOpen)
          }
          onMatches={() => openTool("matches", setPlannerOpen)}
          onPositions={() => openTool("positions", setPositionsOpen)}
          onSummary={() => openTool("summary", setSummaryOpen)}
          programmingCenterAvailable={programmingCenterAvailable}
          onProgrammingCenter={() => setProgrammingCenterOpen(true)}
        />
      ) : null}

      <div className="grid grid-cols-1 justify-items-center gap-4 md:grid-cols-2 lg:grid-cols-2">
        {phases.map((phase) => (
          <CardFase key={phase.idphase || phase.idtypephase} phase={phase} />
        ))}
      </div>

      {showActions && toolsContext ? (
        <>
          <ParticipantsManagerModal
            open={participantsOpen}
            onClose={() => setParticipantsOpen(false)}
            idevent={idevent}
            idsport={idsport}
            context={toolsContext}
            onChanged={handleChanged}
          />
          <MatchPlannerModal
            open={plannerOpen}
            onClose={() => setPlannerOpen(false)}
            idevent={idevent}
            idsport={idsport}
            context={toolsContext}
            onChanged={handleChanged}
          />
          <PositionsCreationModal
            open={positionsOpen}
            onClose={() => setPositionsOpen(false)}
            idevent={idevent}
            idsport={idsport}
            context={toolsContext}
            onChanged={handleChanged}
          />
          <PhaseSummaryModal
            open={summaryOpen}
            onClose={() => setSummaryOpen(false)}
            idevent={idevent}
            idsport={idsport}
            context={toolsContext}
            onChanged={handleChanged}
          />
        </>
      ) : null}

      {/* El Centro trae su propio contexto verificado: no depende de `toolsContext` ni de que la
          configuración esté abierta (dentro avisa si lo está). */}
      {programmingCenterAvailable ? (
        <ProgrammingCenterModal
          open={programmingCenterOpen}
          onClose={async () => {
            setProgrammingCenterOpen(false);
            await handleChanged();
          }}
          idevent={idevent}
          idsport={idsport}
          enabled={programmingCenterStatus.enabled}
        />
      ) : null}
    </>
  );
};

export default FasesArea;
